/** @file Throws a structured Pose2DError from core, which imports @pose2d/format as types only. */
import type { ErrorCode, Pose2DError } from '@pose2d/format/types';

/** Optional fields of a core error: template params, a JSON-pointer-like path and a hint. */
export interface CoreFailExtra {
  params?: Record<string, string | number>;
  path?: string;
  hint?: string;
}

/**
 * Never returns: throws `Error("<message>[ at <path>][: <hint>]")` carrying `.pose2d` (read it back with
 * `@pose2d/format/toPose2DError`). `message` is English: for NOT_FOUND and MOUNT_INVALID it is the ERROR_CODES
 * template with `params` filled in; for VALIDATION it is a specific sentence (a UI that re-renders from `code`
 * shows the generic template) and the JSON path goes in `path`, never in the message. Omit `path` for the root.
 */
export function coreFail(code: ErrorCode, message: string, extra: CoreFailExtra = {}): never {
  const pose2d: Pose2DError = {
    code,
    message,
    ...(extra.params === undefined ? {} : { params: extra.params }),
    ...(extra.path === undefined ? {} : { path: extra.path }),
    ...(extra.hint === undefined ? {} : { hint: extra.hint }),
  };
  const text = message + (extra.path === undefined ? '' : ` at ${extra.path}`) + (extra.hint === undefined ? '' : `: ${extra.hint}`);
  throw Object.assign(new Error(text), { pose2d });
}
